import { supabase } from '../supabaseClient';

/** Override with REACT_APP_API_TIMEOUT_MS (must restart yarn start; value is inlined at compile time). */
const _timeoutParsed = Number(process.env.REACT_APP_API_TIMEOUT_MS);
const BACKEND_TIMEOUT_MS =
  Number.isFinite(_timeoutParsed) && _timeoutParsed > 0 ? _timeoutParsed : 90000;

/**
 * Resolve API base URL.
 * - In development: if REACT_APP_DEV_USE_PROXY is 1/true, always returns '' so the browser uses
 *   same-origin /api → craco proxy → 127.0.0.1:8001 (fixes Failed to fetch when BACKEND_URL is a LAN
 *   IP but uvicorn only binds to loopback). Otherwise, if REACT_APP_BACKEND_URL is set, the browser
 *   calls that origin directly (needs uvicorn --host 0.0.0.0 and CORS). If unset, returns '' (proxy).
 * - In production: REACT_APP_BACKEND_URL (with :8001 default for plain http URLs).
 */
export function backendBaseUrl() {
  if (process.env.NODE_ENV === 'development') {
    const useProxy =
      process.env.REACT_APP_DEV_USE_PROXY === '1' ||
      String(process.env.REACT_APP_DEV_USE_PROXY || '').toLowerCase() === 'true';
    if (useProxy) {
      return '';
    }
    const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
    if (raw) {
      const trimmed = raw.replace(/\/$/, '');
      try {
        const url = new URL(trimmed.includes('://') ? trimmed : `http://${trimmed}`);
        if (!url.port && url.protocol === 'http:') {
          url.port = '8001';
        }
        return url.origin;
      } catch {
        return trimmed;
      }
    }
    return '';
  }

  const raw = (process.env.REACT_APP_BACKEND_URL || '').trim();
  if (!raw) return '';
  const trimmed = raw.replace(/\/$/, '');
  try {
    const url = new URL(trimmed.includes('://') ? trimmed : `http://${trimmed}`);
    if (!url.port && url.protocol === 'http:') {
      url.port = '8001';
    }
    return url.origin;
  } catch {
    return trimmed;
  }
}

function networkErrorHint(err) {
  const msg = err?.message || '';
  if (msg === 'Failed to fetch' || msg === 'Load failed' || err?.name === 'TypeError') {
    if (process.env.NODE_ENV === 'development') {
      return (
        'Cannot reach the API. For yarn start: set REACT_APP_DEV_USE_PROXY=1 in frontend/.env (uses /api → 127.0.0.1:8001), ' +
        'run uvicorn on this machine (e.g. uvicorn server:app --host 127.0.0.1 --port 8001), and restart yarn start. ' +
        'POST /api failing only: leave ENABLE_VISUAL_EDITS unset (visual-edits middleware breaks proxied JSON POSTs). ' +
        'Or use direct LAN: uvicorn --host 0.0.0.0 --port 8001 and REACT_APP_BACKEND_URL=http://YOUR_LAN_IP:8001 with CORS allowing your dev origin.'
      );
    }
    return (
      'Cannot reach the API. Set REACT_APP_BACKEND_URL to your API origin with port (e.g. https://api.example.com).'
    );
  }
  return msg || 'Network error';
}

/** Best-effort message from a failed API response (JSON FastAPI `detail` or raw body / status). */
function messageFromFailedResponse(status, text) {
  let parsed = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      /* non-JSON e.g. HTML from proxy */
    }
  }
  if (parsed && typeof parsed === 'object') {
    const d = parsed.detail ?? parsed.message ?? parsed.error;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) {
      const parts = d.map((x) =>
        typeof x === 'object' && x?.msg != null ? x.msg : JSON.stringify(x)
      );
      if (parts.length) return parts.join('; ');
    }
    if (d != null && typeof d === 'object' && d.message) return String(d.message);
    if (d != null) return JSON.stringify(d);
  }
  const trimmed = (text || '').trim();
  if (trimmed) return trimmed.length > 400 ? `${trimmed.slice(0, 400)}…` : trimmed;
  return `Request failed (${status})`;
}

/**
 * Load or create public.users via FastAPI (service role). Call after OAuth so each account gets a row.
 * @param {string} accessToken
 * @param {{ timeoutMs?: number }} [options]
 * @returns {{ user: object|null, error: string|null, skipped: boolean }}
 */
export async function syncAppUserFromBackend(accessToken, options = {}) {
  if (!accessToken) {
    return { user: null, error: null, skipped: true };
  }
  const base = backendBaseUrl();
  // In dev, base is '' on purpose (same-origin + craco /api proxy). Do not treat as "missing env".
  if (process.env.NODE_ENV !== 'development' && base === '') {
    return { user: null, error: null, skipped: true };
  }

  const timeoutMs = options.timeoutMs ?? BACKEND_TIMEOUT_MS;
  const controller = new AbortController();
  const t = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${base}/api/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: controller.signal,
    });
    clearTimeout(t);

    if (!response.ok) {
      const text = await response.text();
      return {
        user: null,
        error: messageFromFailedResponse(response.status, text),
        skipped: false,
      };
    }

    const data = await response.json();
    return { user: data.user ?? null, error: null, skipped: false };
  } catch (e) {
    clearTimeout(t);
    if (e?.name === 'AbortError') {
      return {
        user: null,
        error:
          'API timed out. From a phone or another PC, localhost does not reach your dev machine — set REACT_APP_BACKEND_URL to this computer\'s LAN IP (e.g. http://192.168.1.10:8001).',
        skipped: false,
      };
    }
    return {
      user: null,
      error: networkErrorHint(e),
      skipped: false,
    };
  }
}

/**
 * Fetch wrapper that automatically includes Supabase auth token
 */
export async function apiFetch(endpoint, options = {}) {
  const { timeoutMs = BACKEND_TIMEOUT_MS, ...fetchOptions } = options;
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    throw new Error('Not authenticated');
  }
  
  const token = session.access_token;

  const headers = {
    Authorization: `Bearer ${token}`,
    ...fetchOptions.headers,
  };
  if (fetchOptions.body != null && fetchOptions.body !== '') {
    headers['Content-Type'] = 'application/json';
  }

  const base = backendBaseUrl();
  if (process.env.NODE_ENV !== 'development' && base === '') {
    throw new Error('REACT_APP_BACKEND_URL is not set');
  }

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response;
  try {
    response = await fetch(`${base}${endpoint}`, {
      ...fetchOptions,
      headers,
      signal: controller.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (e?.name === 'AbortError') {
      throw new Error(
        process.env.NODE_ENV === 'development'
          ? `Request timed out (${BACKEND_TIMEOUT_MS}ms). Run uvicorn on 127.0.0.1:8001 (proxy target). Ensure SUPABASE_* in backend/.env and restart yarn start if you changed REACT_APP_API_TIMEOUT_MS.`
          : 'Request timed out. Check your network and API availability.'
      );
    }
    throw new Error(networkErrorHint(e));
  }
  clearTimeout(timer);
  
  if (!response.ok) {
    const text = await response.text();
    throw new Error(messageFromFailedResponse(response.status, text));
  }
  
  return response.json();
}
